/* ═══════════════════════════════════════════════════════════════
   EmptyState — reusable "no content" / "no data" placeholder.

   Use when a surface has nothing meaningful to show (no rows, no
   data in range, no results) instead of rendering a blank frame or
   alarming zero-value noise. Satisfies the `empty-states` and
   `empty-data-state` rules — see docs/DESIGN.md §5.

   Props:
     - icon?    : inline SVG node (defaults to a neutral chart glyph).
                  Never an emoji — rule `no-emoji-icons`, docs/DESIGN.md §5.
     - title    : short headline ("No time logged in this range").
     - subtitle?: one-line hint toward a fix ("Try a wider date range").
     - action?  : optional CTA { label, onClick }.

   Styling uses the app's semantic CSS variables only:
     --sf surface · --bd border · --tx text · --tm muted text
     --ac accent · --acr accent rgb triple.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
}

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: EmptyStateAction;
}

// Neutral default glyph: a bar-chart outline. Inline SVG, currentColor,
// so it inherits the muted accent tint below. No emoji.
const DefaultIcon = (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M3 3v18h18" />
    <rect x="7" y="12" width="3" height="5" rx="0.5" />
    <rect x="13" y="8" width="3" height="9" rx="0.5" />
    <rect x="19" y="14" width="0.01" height="0.01" />
  </svg>
);

export default function EmptyState({ icon, title, subtitle, action }: EmptyStateProps) {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 6,
        padding: '48px 24px',
        minHeight: 200,
        background: 'var(--sf)',
        border: '1px dashed var(--bd)',
        borderRadius: 8,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 52,
          height: 52,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          marginBottom: 6,
          background: 'rgba(var(--acr), 0.10)',
          color: 'var(--ac)',
        }}
      >
        {icon ?? DefaultIcon}
      </div>

      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx)' }}>{title}</div>

      {subtitle && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--tm)',
            maxWidth: 360,
            lineHeight: 1.5,
          }}
        >
          {subtitle}
        </div>
      )}

      {action && (
        <button
          type="button"
          onClick={action.onClick}
          style={{
            marginTop: 14,
            padding: '8px 16px',
            borderRadius: 6,
            border: '1px solid var(--ac)',
            background: 'var(--ac)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
